import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import awsConfig from "../aws-config";
import { resolveAuthToken } from "../utils/authToken";
import {
  COLLABORATION_MILESTONE_DECISION_STATES,
  COLLABORATION_MILESTONE_FINDING_TYPES,
  buildCollaborationMilestoneGatecheckRecord,
  buildCollaborationMilestoneTemplateMeta,
  createCollaborationMilestoneBlankWorkspace,
  duplicateMilestoneAction,
  duplicateMilestoneFinding,
  duplicateMilestoneHistoryEntry,
  getCollaborationMilestonePresetOptions,
  validateCollaborationMilestoneWorkspace,
} from "../utils/collaborationMilestoneGatecheckBuilder";
import "./GatecheckTemplatePage.css";

const GET_MENU_API_URL = awsConfig.api_url_get_menu;
const TEMPLATE_NAME = "collaboration_milestone_gatecheck";

const organisationTypeOptions = [
  "Biotechnology",
  "Biopharmaceutical",
  "Pharmaceutical",
  "Research organisation",
  "Platform company",
  "Other",
];

const collaborationTypeOptions = [
  "Collaboration and licence",
  "Exclusive licence",
  "Research collaboration",
  "Co-development",
  "Option-to-license",
  "Discovery collaboration",
  "Strategic partnership",
  "Other",
];

function sanitizeSlug(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "")
    .slice(0, 50);
}

function prettyJson(value) {
  return JSON.stringify(value, null, 2);
}

function buildStorageKey(businessId, suffix) {
  return `collaboration-milestone-gatecheck:${businessId || "default"}:${suffix}`;
}

function parseSavedJson(raw, fallback) {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function SavedProfileSelector({
  savedProfiles,
  selectedProfileId,
  onSelect,
  onDelete,
}) {
  if (!savedProfiles.length) return null;
  return (
    <div className="gatecheck-template-page__saved-tools">
      <label>
        Saved collaboration context
        <select
          value={selectedProfileId}
          onChange={(event) => onSelect(event.target.value)}
        >
          <option value="">Choose a saved context</option>
          {savedProfiles.map((profile) => (
            <option key={profile.profileId} value={profile.profileId}>
              {profile.organisationName || "Untitled context"}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        className="gatecheck-template-page__ghost-button"
        onClick={() => onDelete(selectedProfileId)}
        disabled={!selectedProfileId}
      >
        Delete saved context
      </button>
    </div>
  );
}

function ChecklistCard({ title, items }) {
  return (
    <section className="gatecheck-template-page__card">
      <h2>{title}</h2>
      <ul className="gatecheck-template-page__checklist">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </section>
  );
}

export default function CollaborationMilestoneGatecheckPage({ business }) {
  const initialWorkspace = useMemo(
    () => createCollaborationMilestoneBlankWorkspace(),
    []
  );
  const [selectedPresetKey, setSelectedPresetKey] = useState("");
  const [collaborationContext, setCollaborationContext] = useState(
    initialWorkspace.collaborationContext
  );
  const [milestoneDefinition, setMilestoneDefinition] = useState(
    initialWorkspace.milestoneDefinition
  );
  const [decisionDraft, setDecisionDraft] = useState(
    initialWorkspace.decisionDraft
  );
  const [storeSlug, setStoreSlug] = useState(initialWorkspace.storeSlug);
  const [publishes, setPublishes] = useState([]);
  const [savedProfiles, setSavedProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [error, setError] = useState("");
  const [profileMessage, setProfileMessage] = useState("");
  const [publishResult, setPublishResult] = useState(null);

  const presets = useMemo(() => getCollaborationMilestonePresetOptions(), []);
  const storageKeys = useMemo(
    () => ({
      profiles: buildStorageKey(business?.businessId, "profiles"),
      draft: buildStorageKey(business?.businessId, "draft"),
    }),
    [business?.businessId]
  );

  const getBearer = useCallback(async () => {
    const { tokens } = await fetchAuthSession();
    const bearer = resolveAuthToken(tokens);
    if (!bearer) {
      throw new Error("Session expired. Please sign in again.");
    }
    return bearer;
  }, []);

  const loadPublishes = useCallback(async () => {
    if (!business?.businessId) return;
    setLoading(true);
    setError("");
    try {
      const bearer = await getBearer();
      const response = await fetch(`${GET_MENU_API_URL}/${business.businessId}`, {
        headers: { Authorization: `Bearer ${bearer}` },
      });
      const body = await response.json();
      if (!response.ok) {
        throw new Error(body?.error || "Failed to load your published links.");
      }
      const active = body?.publishes?.active || [];
      setPublishes(
        active.filter(
          (entry) => (entry.template || "").toLowerCase() === TEMPLATE_NAME
        )
      );
    } catch (err) {
      setError(err.message || "Failed to load your published links.");
    } finally {
      setLoading(false);
    }
  }, [business?.businessId, getBearer]);

  useEffect(() => {
    loadPublishes();
  }, [loadPublishes]);

  useEffect(() => {
    const draft = parseSavedJson(
      window.localStorage.getItem(storageKeys.draft),
      null
    );
    if (draft) {
      setSelectedPresetKey(draft.selectedPresetKey || "");
      setCollaborationContext(
        draft.collaborationContext || initialWorkspace.collaborationContext
      );
      setMilestoneDefinition(
        draft.milestoneDefinition || initialWorkspace.milestoneDefinition
      );
      setDecisionDraft(draft.decisionDraft || initialWorkspace.decisionDraft);
      setStoreSlug(draft.storeSlug || initialWorkspace.storeSlug);
    }
    setSavedProfiles(
      parseSavedJson(window.localStorage.getItem(storageKeys.profiles), [])
    );
  }, [
    initialWorkspace.collaborationContext,
    initialWorkspace.decisionDraft,
    initialWorkspace.milestoneDefinition,
    initialWorkspace.storeSlug,
    storageKeys.draft,
    storageKeys.profiles,
  ]);

  useEffect(() => {
    const draftPayload = {
      selectedPresetKey,
      collaborationContext,
      milestoneDefinition,
      decisionDraft,
      storeSlug,
    };
    window.localStorage.setItem(storageKeys.draft, JSON.stringify(draftPayload));
  }, [
    collaborationContext,
    decisionDraft,
    milestoneDefinition,
    selectedPresetKey,
    storageKeys.draft,
    storeSlug,
  ]);

  const record = useMemo(
    () =>
      buildCollaborationMilestoneGatecheckRecord(
        collaborationContext,
        milestoneDefinition,
        decisionDraft
      ),
    [collaborationContext, decisionDraft, milestoneDefinition]
  );

  const validationErrors = useMemo(
    () =>
      validateCollaborationMilestoneWorkspace(
        collaborationContext,
        milestoneDefinition,
        decisionDraft
      ),
    [collaborationContext, decisionDraft, milestoneDefinition]
  );

  const updateContext = (field, value) => {
    setCollaborationContext((current) => ({ ...current, [field]: value }));
  };

  const updateMilestone = (field, value) => {
    setMilestoneDefinition((current) => ({ ...current, [field]: value }));
  };

  const updateDecision = (field, value) => {
    setDecisionDraft((current) => ({ ...current, [field]: value }));
  };

  const updateFinding = (index, field, value) => {
    setDecisionDraft((current) => ({
      ...current,
      findings: current.findings.map((finding, findingIndex) =>
        findingIndex === index ? { ...finding, [field]: value } : finding
      ),
    }));
  };

  const updateAction = (index, field, value) => {
    setDecisionDraft((current) => ({
      ...current,
      nextActions: current.nextActions.map((action, actionIndex) =>
        actionIndex === index ? { ...action, [field]: value } : action
      ),
    }));
  };

  const updateHistoryEntry = (index, field, value) => {
    setDecisionDraft((current) => ({
      ...current,
      decisionHistory: current.decisionHistory.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, [field]: value } : entry
      ),
    }));
  };

  const addFinding = () => {
    setDecisionDraft((current) => ({
      ...current,
      findings: [...current.findings, duplicateMilestoneFinding()],
    }));
  };

  const removeFinding = (index) => {
    setDecisionDraft((current) => ({
      ...current,
      findings:
        current.findings.length === 1
          ? [duplicateMilestoneFinding()]
          : current.findings.filter((_, findingIndex) => findingIndex !== index),
    }));
  };

  const addAction = () => {
    setDecisionDraft((current) => ({
      ...current,
      nextActions: [...current.nextActions, duplicateMilestoneAction()],
    }));
  };

  const removeAction = (index) => {
    setDecisionDraft((current) => ({
      ...current,
      nextActions:
        current.nextActions.length === 1
          ? [duplicateMilestoneAction()]
          : current.nextActions.filter((_, actionIndex) => actionIndex !== index),
    }));
  };

  const addHistoryEntry = () => {
    setDecisionDraft((current) => ({
      ...current,
      decisionHistory: [
        ...current.decisionHistory,
        duplicateMilestoneHistoryEntry(),
      ],
    }));
  };

  const removeHistoryEntry = (index) => {
    setDecisionDraft((current) => ({
      ...current,
      decisionHistory:
        current.decisionHistory.length === 1
          ? [duplicateMilestoneHistoryEntry()]
          : current.decisionHistory.filter((_, entryIndex) => entryIndex !== index),
    }));
  };

  const saveProfile = () => {
    if (!collaborationContext.organisationName.trim()) {
      setProfileMessage("Add an organisation name before saving the reusable context.");
      return;
    }
    setSavingProfile(true);
    const profileId =
      collaborationContext.profileId ||
      `profile-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const payload = {
      ...collaborationContext,
      profileId,
      savedAt: new Date().toISOString(),
    };
    const nextProfiles = [...savedProfiles];
    const existingIndex = nextProfiles.findIndex(
      (profile) => profile.profileId === profileId
    );
    if (existingIndex >= 0) {
      nextProfiles[existingIndex] = payload;
    } else {
      nextProfiles.unshift(payload);
    }
    setSavedProfiles(nextProfiles);
    setSelectedProfileId(profileId);
    setCollaborationContext((current) => ({ ...current, profileId }));
    window.localStorage.setItem(storageKeys.profiles, JSON.stringify(nextProfiles));
    setProfileMessage("Collaboration context saved for reuse.");
    setSavingProfile(false);
  };

  const loadSavedProfile = (profileId) => {
    setSelectedProfileId(profileId);
    if (!profileId) return;
    const profile = savedProfiles.find((entry) => entry.profileId === profileId);
    if (!profile) return;
    setCollaborationContext(profile);
    setProfileMessage("Saved collaboration context loaded into the builder.");
  };

  const deleteSavedProfile = (profileId) => {
    if (!profileId) return;
    const nextProfiles = savedProfiles.filter(
      (profile) => profile.profileId !== profileId
    );
    setSavedProfiles(nextProfiles);
    setSelectedProfileId("");
    window.localStorage.setItem(storageKeys.profiles, JSON.stringify(nextProfiles));
    if (collaborationContext.profileId === profileId) {
      setCollaborationContext((current) => ({ ...current, profileId: "" }));
    }
    setProfileMessage("Saved collaboration context deleted.");
  };

  const resetWorkspace = () => {
    const blank = createCollaborationMilestoneBlankWorkspace();
    setSelectedPresetKey("");
    setCollaborationContext(blank.collaborationContext);
    setMilestoneDefinition(blank.milestoneDefinition);
    setDecisionDraft(blank.decisionDraft);
    setStoreSlug(blank.storeSlug);
    setSelectedProfileId("");
    setPublishResult(null);
    setProfileMessage("Builder reset to a blank workspace.");
  };

  const publishFlow = async (event) => {
    event.preventDefault();
    setPublishing(true);
    setError("");
    setPublishResult(null);

    try {
      const trimmedSlug = sanitizeSlug(storeSlug);
      if (!trimmedSlug) {
        throw new Error("Public slug is required.");
      }
      if (validationErrors.length) {
        throw new Error(validationErrors[0]);
      }
      const bearer = await getBearer();
      const publishRecord = {
        ...record,
        lastUpdated: new Date().toISOString(),
      };
      const response = await fetch(`${GET_MENU_API_URL}/${business.businessId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${bearer}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          template: TEMPLATE_NAME,
          storeSlug: trimmedSlug,
          items: [],
          components: [],
          templateMeta: buildCollaborationMilestoneTemplateMeta(publishRecord),
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          body.error || "Could not publish the collaboration milestone gatecheck."
        );
      }
      setStoreSlug(trimmedSlug);
      setPublishResult(body);
      setDecisionDraft((current) => ({
        ...current,
        lastUpdated: publishRecord.lastUpdated,
      }));
      await loadPublishes();
    } catch (err) {
      setError(
        err.message ||
          "Could not publish the collaboration milestone gatecheck."
      );
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="gatecheck-template-page">
      <div className="gatecheck-template-page__header">
        <div>
          <p className="gatecheck-template-page__eyebrow">Layerbound Template</p>
          <h1>Collaboration Milestone Gatecheck</h1>
          <p className="gatecheck-template-page__copy">
            Determine whether a collaboration milestone is ready to be formally
            declared, with agreement requirements, evidence, gaps, owners and
            approvals in one live decision record.
          </p>
        </div>
        <div className="gatecheck-template-page__header-actions">
          <button
            type="button"
            className="gatecheck-template-page__ghost-button"
            onClick={resetWorkspace}
          >
            New blank workspace
          </button>
          <Link className="gatecheck-template-page__back" to="/">
            Back to dashboard
          </Link>
        </div>
      </div>

      <div className="gatecheck-template-page__grid">
        <div className="gatecheck-template-page__main">
          <section className="gatecheck-template-page__card">
            <div className="gatecheck-template-page__section-head">
              <div>
                <h2>1. Start point</h2>
                <p className="gatecheck-template-page__muted">
                  Start from a blank collaboration milestone workspace. Presets can
                  be added later without changing the publish flow.
                </p>
              </div>
            </div>
            <div className="gatecheck-template-page__form-grid gatecheck-template-page__form-grid--two">
              <label>
                Starter preset
                <select
                  value={selectedPresetKey}
                  onChange={(event) => setSelectedPresetKey(event.target.value)}
                >
                  <option value="">Blank workspace</option>
                  {presets.map((preset) => (
                    <option key={preset.key} value={preset.key}>
                      {preset.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Public slug
                <input
                  value={storeSlug}
                  onChange={(event) => setStoreSlug(sanitizeSlug(event.target.value))}
                  placeholder="milestone-gatecheck"
                />
              </label>
            </div>
          </section>

          <section className="gatecheck-template-page__card">
            <div className="gatecheck-template-page__section-head">
              <div>
                <h2>2. Collaboration context</h2>
                <p className="gatecheck-template-page__muted">
                  Capture the reusable organisation and collaboration context used
                  to evaluate milestone readiness.
                </p>
              </div>
              <button
                type="button"
                className="gatecheck-template-page__ghost-button"
                onClick={saveProfile}
                disabled={savingProfile}
              >
                {savingProfile ? "Saving..." : "Save collaboration context"}
              </button>
            </div>

            <SavedProfileSelector
              savedProfiles={savedProfiles}
              selectedProfileId={selectedProfileId}
              onSelect={loadSavedProfile}
              onDelete={deleteSavedProfile}
            />

            <div className="gatecheck-template-page__form-grid gatecheck-template-page__form-grid--two">
              <label>
                Organisation
                <input
                  value={collaborationContext.organisationName}
                  onChange={(event) =>
                    updateContext("organisationName", event.target.value)
                  }
                  placeholder="OMass Therapeutics"
                />
              </label>
              <label>
                Short name
                <input
                  value={collaborationContext.organisationShortName}
                  onChange={(event) =>
                    updateContext("organisationShortName", event.target.value)
                  }
                  placeholder="OMass"
                />
              </label>
              <label>
                Organisation type
                <select
                  value={collaborationContext.organisationType}
                  onChange={(event) =>
                    updateContext("organisationType", event.target.value)
                  }
                >
                  {organisationTypeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Legal entity
                <input
                  value={collaborationContext.legalEntity}
                  onChange={(event) =>
                    updateContext("legalEntity", event.target.value)
                  }
                  placeholder="Legal entity"
                />
              </label>
              <label>
                Website
                <input
                  value={collaborationContext.website}
                  onChange={(event) => updateContext("website", event.target.value)}
                  placeholder="https://example.com"
                />
              </label>
              <label>
                Location
                <input
                  value={collaborationContext.location}
                  onChange={(event) => updateContext("location", event.target.value)}
                  placeholder="Oxford, UK"
                />
              </label>
              <label>
                Partner / Counterparty
                <input
                  value={collaborationContext.partnerName}
                  onChange={(event) => updateContext("partnerName", event.target.value)}
                  placeholder="Genentech"
                />
              </label>
              <label>
                Partner short name
                <input
                  value={collaborationContext.partnerShortName}
                  onChange={(event) =>
                    updateContext("partnerShortName", event.target.value)
                  }
                  placeholder="Genentech"
                />
              </label>
              <label>
                Programme / Asset
                <input
                  value={collaborationContext.programmeName}
                  onChange={(event) =>
                    updateContext("programmeName", event.target.value)
                  }
                  placeholder="Inflammatory bowel disease collaboration"
                />
              </label>
              <label>
                Therapeutic area
                <input
                  value={collaborationContext.therapeuticArea}
                  onChange={(event) =>
                    updateContext("therapeuticArea", event.target.value)
                  }
                  placeholder="Immunology"
                />
              </label>
              <label>
                Collaboration type
                <select
                  value={collaborationContext.collaborationType}
                  onChange={(event) =>
                    updateContext("collaborationType", event.target.value)
                  }
                >
                  {collaborationTypeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Agreement date
                <input
                  type="date"
                  value={collaborationContext.agreementDate}
                  onChange={(event) =>
                    updateContext("agreementDate", event.target.value)
                  }
                />
              </label>
              <label>
                Legal effective date
                <input
                  type="date"
                  value={collaborationContext.legalEffectiveDate}
                  onChange={(event) =>
                    updateContext("legalEffectiveDate", event.target.value)
                  }
                />
              </label>
              <label>
                Current programme stage
                <input
                  value={collaborationContext.currentProgrammeStage}
                  onChange={(event) =>
                    updateContext("currentProgrammeStage", event.target.value)
                  }
                  placeholder="Lead optimisation"
                />
              </label>
              <label>
                Target milestone
                <input
                  value={collaborationContext.targetMilestone}
                  onChange={(event) =>
                    updateContext("targetMilestone", event.target.value)
                  }
                  placeholder="Candidate Selection Handoff"
                />
              </label>
              <label>
                Target gate date
                <input
                  type="date"
                  value={collaborationContext.targetGateDate}
                  onChange={(event) =>
                    updateContext("targetGateDate", event.target.value)
                  }
                />
              </label>
            </div>

            <div className="gatecheck-template-page__form-grid">
              <label>
                Public / disclosed economics
                <textarea
                  rows={3}
                  value={collaborationContext.disclosedEconomics}
                  onChange={(event) =>
                    updateContext("disclosedEconomics", event.target.value)
                  }
                  placeholder="Public milestone economics or disclosed structure."
                />
              </label>
              <label>
                Responsibility model
                <textarea
                  rows={3}
                  value={collaborationContext.responsibilityModel}
                  onChange={(event) =>
                    updateContext("responsibilityModel", event.target.value)
                  }
                  placeholder="Who owns evidence preparation, governance review and notice issuance."
                />
              </label>
              <label>
                Relevant collaborations / references
                <textarea
                  rows={3}
                  value={collaborationContext.relevantCollaborations}
                  onChange={(event) =>
                    updateContext("relevantCollaborations", event.target.value)
                  }
                  placeholder="Comparable programmes, disclosures or prior milestone handling."
                />
              </label>
              <label>
                Key functions involved
                <textarea
                  rows={3}
                  value={collaborationContext.keyFunctionsInvolved}
                  onChange={(event) =>
                    updateContext("keyFunctionsInvolved", event.target.value)
                  }
                  placeholder="Legal, Alliance Management, R&D, CMC, Finance, Regulatory."
                />
              </label>
              <label>
                Governance / operating controls
                <textarea
                  rows={3}
                  value={collaborationContext.governanceControls}
                  onChange={(event) =>
                    updateContext("governanceControls", event.target.value)
                  }
                  placeholder="Steering committees, approval forums, operating procedures."
                />
              </label>
              <label>
                Strengths
                <textarea
                  rows={3}
                  value={collaborationContext.strengths}
                  onChange={(event) => updateContext("strengths", event.target.value)}
                  placeholder="What currently makes this milestone declaration more credible."
                />
              </label>
              <label>
                Unknowns requiring confirmation
                <textarea
                  rows={3}
                  value={collaborationContext.unknowns}
                  onChange={(event) => updateContext("unknowns", event.target.value)}
                  placeholder="Anything still requiring direct confirmation."
                />
              </label>
            </div>

            {profileMessage ? (
              <p className="gatecheck-template-page__message">{profileMessage}</p>
            ) : null}
          </section>

          <section className="gatecheck-template-page__card">
            <div className="gatecheck-template-page__section-head">
              <div>
                <h2>3. Milestone definition</h2>
                <p className="gatecheck-template-page__muted">
                  Define the contractual and operational conditions that must be
                  satisfied before the milestone can be declared.
                </p>
              </div>
            </div>

            <div className="gatecheck-template-page__form-grid gatecheck-template-page__form-grid--two">
              <label>
                Milestone name
                <input
                  value={milestoneDefinition.milestoneName}
                  onChange={(event) =>
                    updateMilestone("milestoneName", event.target.value)
                  }
                  placeholder="Candidate Selection Handoff"
                />
              </label>
              <label>
                Milestone summary
                <input
                  value={milestoneDefinition.milestoneSummary}
                  onChange={(event) =>
                    updateMilestone("milestoneSummary", event.target.value)
                  }
                  placeholder="Plain-English summary of what the milestone represents."
                />
              </label>
            </div>

            <div className="gatecheck-template-page__form-grid">
              <label>
                Milestone description
                <textarea
                  rows={3}
                  value={milestoneDefinition.milestoneDescription}
                  onChange={(event) =>
                    updateMilestone("milestoneDescription", event.target.value)
                  }
                  placeholder="Describe the milestone in scientific, contractual and operational terms."
                />
              </label>
              <label>
                Agreement / amendment sources
                <textarea
                  rows={3}
                  value={milestoneDefinition.agreementSources}
                  onChange={(event) =>
                    updateMilestone("agreementSources", event.target.value)
                  }
                  placeholder="Agreement, amendment, side letter, governance materials."
                />
              </label>
              <label>
                Operative milestone requirement
                <textarea
                  rows={3}
                  value={milestoneDefinition.operativeMilestoneRequirement}
                  onChange={(event) =>
                    updateMilestone("operativeMilestoneRequirement", event.target.value)
                  }
                  placeholder="The exact condition that must be satisfied."
                />
              </label>
              <label>
                Relevant clause(s)
                <textarea
                  rows={2}
                  value={milestoneDefinition.relevantClauses}
                  onChange={(event) =>
                    updateMilestone("relevantClauses", event.target.value)
                  }
                  placeholder="Clause numbers, schedules or amendment references."
                />
              </label>
              <label>
                Required evidence package
                <textarea
                  rows={3}
                  value={milestoneDefinition.requiredEvidencePackage}
                  onChange={(event) =>
                    updateMilestone("requiredEvidencePackage", event.target.value)
                  }
                  placeholder="Data package, memos, approvals, board materials, notices."
                />
              </label>
              <label>
                Governance / approval requirement
                <textarea
                  rows={3}
                  value={milestoneDefinition.governanceApprovalRequirement}
                  onChange={(event) =>
                    updateMilestone("governanceApprovalRequirement", event.target.value)
                  }
                  placeholder="Committees, sign-offs or partner approvals needed."
                />
              </label>
              <label>
                Responsibility transition
                <textarea
                  rows={3}
                  value={milestoneDefinition.responsibilityTransition}
                  onChange={(event) =>
                    updateMilestone("responsibilityTransition", event.target.value)
                  }
                  placeholder="What changes operationally once the milestone is declared."
                />
              </label>
              <label>
                Contractual trigger
                <textarea
                  rows={3}
                  value={milestoneDefinition.contractualTrigger}
                  onChange={(event) =>
                    updateMilestone("contractualTrigger", event.target.value)
                  }
                  placeholder="The contractual event or threshold tied to declaration."
                />
              </label>
              <label>
                Notice requirements
                <textarea
                  rows={3}
                  value={milestoneDefinition.noticeRequirements}
                  onChange={(event) =>
                    updateMilestone("noticeRequirements", event.target.value)
                  }
                  placeholder="Notice timing, content and delivery requirements."
                />
              </label>
              <label>
                Payment / financial implication
                <textarea
                  rows={3}
                  value={milestoneDefinition.paymentImplication}
                  onChange={(event) =>
                    updateMilestone("paymentImplication", event.target.value)
                  }
                  placeholder="Milestone payment, revenue recognition or partner charge implications."
                />
              </label>
              <label>
                IP / third-party-rights requirements
                <textarea
                  rows={3}
                  value={milestoneDefinition.ipRequirements}
                  onChange={(event) =>
                    updateMilestone("ipRequirements", event.target.value)
                  }
                  placeholder="Any IP consents, licences or third-party-rights dependencies."
                />
              </label>
              <label>
                Handoff deliverables
                <textarea
                  rows={3}
                  value={milestoneDefinition.handoffDeliverables}
                  onChange={(event) =>
                    updateMilestone("handoffDeliverables", event.target.value)
                  }
                  placeholder="Data room items, reports, minutes, transfer packs."
                />
              </label>
              <label>
                Assumptions & limitations
                <textarea
                  rows={3}
                  value={milestoneDefinition.assumptionsLimitations}
                  onChange={(event) =>
                    updateMilestone("assumptionsLimitations", event.target.value)
                  }
                  placeholder="Representative demo wording, assumptions and limits of the current record."
                />
              </label>
            </div>
          </section>

          <section className="gatecheck-template-page__card">
            <div className="gatecheck-template-page__section-head">
              <div>
                <h2>4. Gate decision</h2>
                <p className="gatecheck-template-page__muted">
                  Capture the current milestone decision, rationale and
                  evidence-backed follow-up.
                </p>
              </div>
            </div>

            <div className="gatecheck-template-page__form-grid gatecheck-template-page__form-grid--two">
              <label>
                Decision status
                <select
                  value={decisionDraft.status}
                  onChange={(event) => updateDecision("status", event.target.value)}
                >
                  {COLLABORATION_MILESTONE_DECISION_STATES.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Badge
                <input
                  value={decisionDraft.badge}
                  onChange={(event) => updateDecision("badge", event.target.value)}
                  placeholder="Manual milestone gatecheck"
                />
              </label>
              <label>
                Confidence
                <select
                  value={decisionDraft.confidence}
                  onChange={(event) =>
                    updateDecision("confidence", event.target.value)
                  }
                >
                  {["Low", "Medium", "High"].map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Decision owner
                <input
                  value={decisionDraft.decisionOwner}
                  onChange={(event) =>
                    updateDecision("decisionOwner", event.target.value)
                  }
                  placeholder="Alliance lead"
                />
              </label>
              <label className="gatecheck-template-page__field-span-2">
                Recommendation
                <input
                  value={decisionDraft.recommendation}
                  onChange={(event) =>
                    updateDecision("recommendation", event.target.value)
                  }
                  placeholder="Do not declare the milestone yet."
                />
              </label>
              <label>
                Human decision
                <input
                  value={decisionDraft.humanDecision}
                  onChange={(event) =>
                    updateDecision("humanDecision", event.target.value)
                  }
                  placeholder="Awaiting governance review"
                />
              </label>
              <label>
                Approval status
                <input
                  value={decisionDraft.approvalStatus}
                  onChange={(event) =>
                    updateDecision("approvalStatus", event.target.value)
                  }
                  placeholder="Awaiting sign-off"
                />
              </label>
              <label className="gatecheck-template-page__field-span-2">
                Rationale
                <textarea
                  rows={3}
                  value={decisionDraft.rationale}
                  onChange={(event) => updateDecision("rationale", event.target.value)}
                  placeholder="Why the current milestone decision is justified."
                />
              </label>
              <label className="gatecheck-template-page__field-span-2">
                Share-view collaboration summary
                <textarea
                  rows={3}
                  value={decisionDraft.opportunitySummary}
                  onChange={(event) =>
                    updateDecision("opportunitySummary", event.target.value)
                  }
                  placeholder="What this milestone decision means in plain language."
                />
              </label>
              <label className="gatecheck-template-page__field-span-2">
                Override reason
                <textarea
                  rows={2}
                  value={decisionDraft.overrideReason}
                  onChange={(event) =>
                    updateDecision("overrideReason", event.target.value)
                  }
                  placeholder="Optional. Explain any difference between recommendation and human decision."
                />
              </label>
            </div>

            <div className="gatecheck-template-page__stack">
              <div className="gatecheck-template-page__subsection-head">
                <h3>Findings</h3>
                <button
                  type="button"
                  className="gatecheck-template-page__ghost-button"
                  onClick={addFinding}
                >
                  Add finding
                </button>
              </div>
              {decisionDraft.findings.map((finding, index) => (
                <div
                  key={`finding-${index}`}
                  className="gatecheck-template-page__nested-card"
                >
                  <div className="gatecheck-template-page__subsection-head">
                    <strong>Finding {index + 1}</strong>
                    <button
                      type="button"
                      className="gatecheck-template-page__ghost-button gatecheck-template-page__ghost-button--danger"
                      onClick={() => removeFinding(index)}
                    >
                      Remove
                    </button>
                  </div>
                  <div className="gatecheck-template-page__form-grid gatecheck-template-page__form-grid--three">
                    <label>
                      Type
                      <select
                        value={finding.type}
                        onChange={(event) =>
                          updateFinding(index, "type", event.target.value)
                        }
                      >
                        {COLLABORATION_MILESTONE_FINDING_TYPES.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Severity
                      <select
                        value={finding.severity}
                        onChange={(event) =>
                          updateFinding(index, "severity", event.target.value)
                        }
                      >
                        {["Low", "Medium", "High"].map((severity) => (
                          <option key={severity} value={severity}>
                            {severity}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Owner
                      <input
                        value={finding.owner}
                        onChange={(event) =>
                          updateFinding(index, "owner", event.target.value)
                        }
                        placeholder="Legal"
                      />
                    </label>
                    <label className="gatecheck-template-page__field-span-3">
                      Title
                      <input
                        value={finding.title}
                        onChange={(event) =>
                          updateFinding(index, "title", event.target.value)
                        }
                        placeholder="Formal candidate designation not yet evidenced"
                      />
                    </label>
                    <label className="gatecheck-template-page__field-span-3">
                      Explanation
                      <textarea
                        rows={3}
                        value={finding.explanation}
                        onChange={(event) =>
                          updateFinding(index, "explanation", event.target.value)
                        }
                      />
                    </label>
                    <label className="gatecheck-template-page__field-span-3">
                      Agreement / gate requirement
                      <textarea
                        rows={2}
                        value={finding.gateRequirement}
                        onChange={(event) =>
                          updateFinding(index, "gateRequirement", event.target.value)
                        }
                      />
                    </label>
                    <label>
                      Source / agreement evidence
                      <input
                        value={finding.agreementEvidenceSource}
                        onChange={(event) =>
                          updateFinding(
                            index,
                            "agreementEvidenceSource",
                            event.target.value
                          )
                        }
                        placeholder="Collaboration agreement"
                      />
                    </label>
                    <label>
                      Clause / reference
                      <input
                        value={finding.clauseReference}
                        onChange={(event) =>
                          updateFinding(index, "clauseReference", event.target.value)
                        }
                        placeholder="Section 4.2"
                      />
                    </label>
                    <label>
                      Confidence
                      <select
                        value={finding.confidence}
                        onChange={(event) =>
                          updateFinding(index, "confidence", event.target.value)
                        }
                      >
                        {["Low", "Medium", "High"].map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="gatecheck-template-page__field-span-3">
                      Available evidence
                      <textarea
                        rows={2}
                        value={finding.availableEvidence}
                        onChange={(event) =>
                          updateFinding(index, "availableEvidence", event.target.value)
                        }
                      />
                    </label>
                    <label className="gatecheck-template-page__field-span-3">
                      Evidence source
                      <input
                        value={finding.evidenceSource}
                        onChange={(event) =>
                          updateFinding(index, "evidenceSource", event.target.value)
                        }
                        placeholder="Board pack, memo, data room or disclosure"
                      />
                    </label>
                    <label className="gatecheck-template-page__field-span-3">
                      Why it matters
                      <textarea
                        rows={2}
                        value={finding.whyItMatters}
                        onChange={(event) =>
                          updateFinding(index, "whyItMatters", event.target.value)
                        }
                      />
                    </label>
                    <label className="gatecheck-template-page__field-span-3">
                      Closure requirement
                      <textarea
                        rows={2}
                        value={finding.closureRequirement}
                        onChange={(event) =>
                          updateFinding(index, "closureRequirement", event.target.value)
                        }
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>

            <div className="gatecheck-template-page__stack">
              <div className="gatecheck-template-page__subsection-head">
                <h3>Next actions</h3>
                <button
                  type="button"
                  className="gatecheck-template-page__ghost-button"
                  onClick={addAction}
                >
                  Add action
                </button>
              </div>
              {decisionDraft.nextActions.map((action, index) => (
                <div
                  key={`action-${index}`}
                  className="gatecheck-template-page__nested-card"
                >
                  <div className="gatecheck-template-page__form-grid gatecheck-template-page__form-grid--three">
                    <label>
                      Owner
                      <input
                        value={action.owner}
                        onChange={(event) =>
                          updateAction(index, "owner", event.target.value)
                        }
                        placeholder="Alliance Management"
                      />
                    </label>
                    <label>
                      Due
                      <input
                        value={action.dueLabel}
                        onChange={(event) =>
                          updateAction(index, "dueLabel", event.target.value)
                        }
                        placeholder="Before milestone declaration"
                      />
                    </label>
                    <label>
                      Status
                      <select
                        value={action.status || "Open"}
                        onChange={(event) =>
                          updateAction(index, "status", event.target.value)
                        }
                      >
                        {["Open", "Completed", "Waived"].map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="gatecheck-template-page__inline-actions">
                      <button
                        type="button"
                        className="gatecheck-template-page__ghost-button gatecheck-template-page__ghost-button--danger"
                        onClick={() => removeAction(index)}
                      >
                        Remove
                      </button>
                    </div>
                    <label className="gatecheck-template-page__field-span-3">
                      Action
                      <textarea
                        rows={2}
                        value={action.action}
                        onChange={(event) =>
                          updateAction(index, "action", event.target.value)
                        }
                      />
                    </label>
                    <label className="gatecheck-template-page__field-span-3">
                      Linked finding
                      <input
                        value={action.linkedFinding || ""}
                        onChange={(event) =>
                          updateAction(index, "linkedFinding", event.target.value)
                        }
                        placeholder="Optional. Link to a finding title or id."
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>

            <div className="gatecheck-template-page__stack">
              <div className="gatecheck-template-page__subsection-head">
                <h3>Decision history</h3>
                <button
                  type="button"
                  className="gatecheck-template-page__ghost-button"
                  onClick={addHistoryEntry}
                >
                  Add history entry
                </button>
              </div>
              {decisionDraft.decisionHistory.map((entry, index) => (
                <div
                  key={`history-${index}`}
                  className="gatecheck-template-page__nested-card"
                >
                  <div className="gatecheck-template-page__form-grid gatecheck-template-page__form-grid--three">
                    <label>
                      Date
                      <input
                        type="datetime-local"
                        value={entry.date ? entry.date.slice(0, 16) : ""}
                        onChange={(event) =>
                          updateHistoryEntry(
                            index,
                            "date",
                            event.target.value
                              ? new Date(event.target.value).toISOString()
                              : ""
                          )
                        }
                      />
                    </label>
                    <label>
                      State
                      <select
                        value={entry.state}
                        onChange={(event) =>
                          updateHistoryEntry(index, "state", event.target.value)
                        }
                      >
                        {COLLABORATION_MILESTONE_DECISION_STATES.map((state) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Owner
                      <input
                        value={entry.owner}
                        onChange={(event) =>
                          updateHistoryEntry(index, "owner", event.target.value)
                        }
                        placeholder="Legal"
                      />
                    </label>
                    <label>
                      Origin
                      <input
                        value={entry.origin || ""}
                        onChange={(event) =>
                          updateHistoryEntry(index, "origin", event.target.value)
                        }
                        placeholder="Layerbound assessment"
                      />
                    </label>
                    <label className="gatecheck-template-page__field-span-3">
                      Note
                      <textarea
                        rows={2}
                        value={entry.note}
                        onChange={(event) =>
                          updateHistoryEntry(index, "note", event.target.value)
                        }
                      />
                    </label>
                  </div>
                  <button
                    type="button"
                    className="gatecheck-template-page__ghost-button gatecheck-template-page__ghost-button--danger"
                    onClick={() => removeHistoryEntry(index)}
                  >
                    Remove history entry
                  </button>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="gatecheck-template-page__side">
          <ChecklistCard
            title="What this live decision record shows"
            items={[
              "Current milestone decision",
              "Evidence-backed rationale",
              "Gating issues and protected unknowns",
              "Accountable owner and closure action",
              "Approval status",
              "Live decision history",
            ]}
          />

          <section className="gatecheck-template-page__card">
            <h2>Publish check</h2>
            {validationErrors.length ? (
              <div className="gatecheck-template-page__validation">
                {validationErrors.map((item) => (
                  <p key={item}>{item}</p>
                ))}
              </div>
            ) : (
              <p className="gatecheck-template-page__success">
                Core collaboration milestone requirements are filled. This
                workspace is ready to publish.
              </p>
            )}
            <form
              className="gatecheck-template-page__publish-form"
              onSubmit={publishFlow}
            >
              <button type="submit" disabled={publishing}>
                {publishing
                  ? "Publishing..."
                  : "Publish collaboration milestone gatecheck"}
              </button>
            </form>
            {error ? <p className="gatecheck-template-page__error">{error}</p> : null}
            {publishResult?.publicUrl ? (
              <div className="gatecheck-template-page__result">
                <span>Latest link</span>
                <a href={publishResult.publicUrl} target="_blank" rel="noreferrer">
                  {publishResult.publicUrl}
                </a>
              </div>
            ) : null}
          </section>

          <section className="gatecheck-template-page__card">
            <h2>Derived live record</h2>
            <p className="gatecheck-template-page__muted">
              This is the publish payload assembled from the structured workspace.
            </p>
            <textarea
              className="gatecheck-template-page__json-preview"
              value={prettyJson(record)}
              readOnly
              rows={24}
              spellCheck="false"
            />
          </section>

          <section className="gatecheck-template-page__card">
            <h2>Live links</h2>
            <p className="gatecheck-template-page__muted">
              Published collaboration milestone gatechecks for this business.
            </p>
            {loading ? (
              <p className="gatecheck-template-page__muted">Loading links...</p>
            ) : null}
            {!loading && !publishes.length ? (
              <p className="gatecheck-template-page__muted">
                No live collaboration milestone gatechecks yet.
              </p>
            ) : null}
            <div className="gatecheck-template-page__links">
              {publishes.map((entry) => (
                <article
                  key={entry.publishedAt}
                  className="gatecheck-template-page__link-card"
                >
                  <div>
                    <strong>{entry.storeSlug || entry.storeId}</strong>
                    <p>
                      Published {new Date(entry.publishedAt).toLocaleDateString()}
                    </p>
                  </div>
                  <a href={entry.htmlUrl || entry.publicUrl} target="_blank" rel="noreferrer">
                    Open link
                  </a>
                </article>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
